<template>
	<div class="cassie-vertical-md">
		<SectionCard>
			<template #title>
				Field Configuration
			</template>
			<template #body>
				<div class="cassie-vertical-md">
					<div
						v-if="userFullPermissions"
						class="d-flex cassie-horizontal-md"
					>
						<Dropdown
							v-model="selectedField"
							:search-input.sync="searchInput"
							:items="mappedAvailableFields"
							custom-sort
							label="Select a field to add"
							style="width: 300px; flex: none;"
							multiple
							searchable
							select-all
							@change="updateSelectAll"
							@update:search-input="searchInput = $event"
						>
							<template
								v-if="mappedAvailableFields.length > 1"
								#prepend-item
							>
								<v-list-item
									dense
									@click="toggleSelectAll"
								>
									<v-checkbox
										v-model="selectAllChecked"
										dense
										@click="toggleSelectAll"
									/>
									<v-list-item-title class="pb-2">
										Select All
									</v-list-item-title>
								</v-list-item>
								<v-divider />
							</template>
						</Dropdown>
						<PrimaryActionButton
							:disabled="!selectedField"
							@click="addField"
						>
							Add
						</PrimaryActionButton>
					</div>
					<v-simple-table
						dense
						fixed-header
						height="700"
					>
						<template #default>
							<thead>
								<tr>
									<th
										scope="col"
										class="text-left"
									>
										Visible in Contact Details
									</th>
									<th
										scope="col"
										class="text-left"
									>
										Database Field Name
									</th>
									<th
										scope="col"
										class="text-left"
										style="width: 20%;"
									>
										Field Label On Contact Details Screen
									</th>
									<th
										scope="col"
										class="text-left"
									>
										Read Only
									</th>
									<th
										scope="col"
										class="text-left"
										style="width: 10%;"
									>
										Field Type
									</th>
									<th
										scope="col"
										class="text-left"
										style="width: 20%;"
									>
										Allowed Values
									</th>
									<th
										scope="col"
										class="text-left"
									>
										Is Mandatory
									</th>
									<th
										scope="col"
										class="text-left"
									>
										Visible in Overview
									</th>
									<th
										scope="col"
										class="text-left"
									>
										Visible in Create Screen
									</th>
									<th
										scope="col"
										class="text-left"
									>
										Is Mandatory in Create Screen
									</th>
									<th
										scope="col"
										class="text-center"
										style="width: 80px"
									>
										Action
									</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(contactDetailsField, index) in contactDetailsFields"
									:key="contactDetailsField.fieldName"
								>
									<td class="text-left">
										<v-switch
											inset
											:disabled="!userFullPermissions"
											:input-value="contactDetailsField.include"
											dense
											@change="updateContactDetailsFieldsProperty(index, 'include', $event)"
										/>
									</td>
									<td
										v-show="false"
										class="text-left"
									>
										{{ contactDetailsField.fieldId }}
									</td>
									<td class="text-left">
										{{ contactDetailsField.fieldName }}
									</td>
									<td
										class="text-left"
										style="width: 15%;"
									>
										<TextField
											:disabled="!userFullPermissions"
											:value="contactDetailsField.fieldLabel"
											:rules="{max:100}"
											small
											label="Field label"
											@input="updateContactDetailsFieldsProperty(index, 'fieldLabel', $event)"
										/>
									</td>
									<td
										v-if="userFullPermissions"
										class="text-left"
									>
										<v-switch
											inset
											:disabled="isSpecialField(contactDetailsField.fieldName)"
											:input-value="contactDetailsField.isReadOnly"
											dense
											@change="updateContactDetailsFieldsProperty(index, 'isReadOnly', $event)"
										/>
									</td>
									<td
										class="text-left"
										style="width: 15%;"
									>
										<Dropdown
											:disabled="isSpecialField(contactDetailsField.fieldName) || !userFullPermissions"
											:value="contactDetailsField.fieldType"
											:items="contactDetailsFieldTypes"
											custom-sort
											label="Field type"
											small
											@input="updateContactDetailsFieldsProperty(index, 'fieldType', $event)"
										/>
									</td>
									<td
										class="text-left"
										style="width: 15%;"
									>
										<TextField
											:disabled="isSpecialField(contactDetailsField.fieldName) || notRegexOrDropdownFieldType(contactDetailsField.fieldType) || !userFullPermissions"
											:value="contactDetailsField.allowedValues ? contactDetailsField.allowedValues.join(',') : ''"
											:rules="{max:1000}"
											small
											label="Allowed values"
											@input="value => updateContactDetailsFieldsProperty(index, 'allowedValues', validatedAllowedValuesValue(contactDetailsField.fieldType, value))"
										/>
									</td>
									<td class="text-left">
										<v-switch
											inset
											:disabled="isSpecialField(contactDetailsField.fieldName) || !userFullPermissions"
											:input-value="contactDetailsField.isMandatory"
											dense
											@change="updateContactDetailsFieldsProperty(index, 'isMandatory', $event)"
										/>
									</td>
									<td class="text-left">
										<v-switch
											inset
											:disabled="!userFullPermissions"
											:input-value="contactDetailsField.showInOverview"
											dense
											@change="updateContactDetailsFieldsProperty(index, 'showInOverview', $event)"
										/>
									</td>
									<td class="text-left">
										<v-switch
											inset
											:disabled="isSpecialField(contactDetailsField.fieldName) || !userFullPermissions"
											:input-value="contactDetailsField.displayInCreateDataSubjectScreen"
											dense
											@change="updateContactDetailsFieldsProperty(index, 'displayInCreateDataSubjectScreen', $event)"
										/>
									</td>
									<td class="text-left">
										<v-switch
											inset
											:disabled="isSpecialField(contactDetailsField.fieldName) || !userFullPermissions"
											:input-value="contactDetailsField.isMandatoryInCreateDataSubjectScreen"
											dense
											@change="updateContactDetailsFieldsProperty(index, 'isMandatoryInCreateDataSubjectScreen', $event)"
										/>
									</td>
									<td class="text-center">
										<v-menu
											v-if="userFullPermissions"
											offset-y
										>
											<template #activator="{ on, attrs }">
												<v-btn
													icon
													v-bind="attrs"
													v-on="on"
													@click.stop
												>
													<v-icon>
														mdi-dots-horizontal
													</v-icon>
												</v-btn>
											</template>
											<v-list dense>
												<v-list-item @click="onRemoveClick(contactDetailsField.fieldName)">
													<v-list-item-content>
														<v-list-item-title>
															Delete
														</v-list-item-title>
													</v-list-item-content>
												</v-list-item>
											</v-list>
										</v-menu>
									</td>
								</tr>
							</tbody>
						</template>
					</v-simple-table>
				</div>
			</template>
		</SectionCard>
		<SectionCard>
			<template #title>
				Functionality
			</template>
			<template #body>
				<v-switch
					inset
					:disabled="!userFullPermissions"
					:input-value="showPostCodeLookup"
					label="Postcode Lookup Enabled"
					@change="$emit('update:showPostCodeLookup', $event)"
				/>
			</template>
		</SectionCard>
	</div>
</template>

<script>
import SectionCard from '../../../../../shared/components/section-card.vue'
import Dropdown from '../../../../../shared/components/dropdown.vue'
import TextField from '../../../../../shared/components/text-field.vue'
import PrimaryActionButton from '../../../../../shared/components/primary-action-button.vue'

let timer

const debounce = func => {
	return (...args) => {
		clearTimeout(timer)
		timer = setTimeout(() => { func.apply(this, args) }, 250)
	}
}

const SPECIAL_FIELD_NAME = 'SyrenisID'
const DROPDOWN_FIELD_TYPE = 2

export default {
	components: { PrimaryActionButton, TextField, Dropdown, SectionCard },
	props: {
		configuration: {
			type: Object,
			default: () => ({})
		},
		contactDetailsFields: {
			type: Array,
			default: () => []
		},
		showPostCodeLookup: {
			type: Boolean,
			default: false
		},
		availableFields: {
			type: Array,
			default: () => []
		},
		userFullPermissions: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			selectedField: null,
			contactDetailsFieldTypes: [
				{ value: 1, text: 'Text Box' },
				{ value: 2, text: 'Dropdown' },
				{ value: 3, text: 'Email' },
				{ value: 4, text: 'Regex' },
				{ value: 5, text: 'Alpha' },
				{ value: 6, text: 'AlphaNum' },
				{ value: 7, text: 'Integer' },
				{ value: 8, text: 'Decimal' }
			].sort((a, b) => a.text.localeCompare(b.text)),
			selectAllChecked: false,
			searchInput: null
		}
	},
	computed: {
		mappedAvailableFields () {
			return this.availableFields
				.filter(({ columnName }) => this.contactDetailsFields.every(({ fieldName }) => columnName.toLowerCase() !== fieldName.toLowerCase()))
				.map(({ columnName }) => ({ value: columnName, text: columnName }))
		}
	},
	methods: {
		updateContactDetailsFieldsProperty (index, property, value) {
			const contactDetailsFields = JSON.parse(JSON.stringify(this.contactDetailsFields))
			contactDetailsFields[index][property] = value
			const isReadOnlyFieldName = 'isReadOnly'
			const isMandatoryFieldName = 'isMandatory'
			const visibleOnScreenFieldName = 'include'
			if (property === isMandatoryFieldName && value === true) {
				contactDetailsFields[index][isReadOnlyFieldName] = false
			} else if (property === isReadOnlyFieldName && value === true) {
				contactDetailsFields[index][isMandatoryFieldName] = false
			} else if (property === visibleOnScreenFieldName && value === false) {
				contactDetailsFields[index][isMandatoryFieldName] = false
			}

			debounce(this.updateContactDetailsFields)(contactDetailsFields)
		},
		onRemoveClick (fieldName) {
			const contactDetailsFields = this.contactDetailsFields.filter(field => field.fieldName !== fieldName)
			this.updateContactDetailsFields(contactDetailsFields)
		},
		addField () {
			const selectedFields = this.selectedField.map(field => {
				return {
					fieldName: field,
					fieldLabel: '',
					fieldId: 0,
					include: true,
					showInOverview: false,
					isMandatory: false,
					isReadOnly: false,
					displayInCreateDataSubjectScreen: false,
					isMandatoryInCreateDataSubjectScreen: false,
					fieldType: 1,
					allowedValues: []
				}
			})
			this.updateContactDetailsFields([
				...this.contactDetailsFields,
				...selectedFields
			])
			this.selectedField = null
			this.searchInput = null
			this.selectAllChecked = false
		},
		updateContactDetailsFields (contactDetails) {
			const updatedContactDetailsFields = [...contactDetails]
			this.updateConfiguration('contactDetailsFields', updatedContactDetailsFields)
		},
		updateConfiguration (key, value) {
			const configuration = {
				...this.configuration,
				[key]: value
			}
			this.$emit('update-configuration', configuration)
		},
		isSpecialField (fieldName) {
			return fieldName.toString().toLowerCase() === SPECIAL_FIELD_NAME.toLowerCase()
		},
		notRegexOrDropdownFieldType (fieldType) {
			const dropdownType = 2
			const regexType = 4
			if (fieldType === dropdownType) return false
			else if (fieldType === regexType) return false
			else return true
		},
		validatedAllowedValuesValue (fieldType, value) {
			if (value === '') return []
			if (fieldType === DROPDOWN_FIELD_TYPE) return value.split(',')
			return [value]
		},
		toggleSelectAll () {
			if (this.selectAllChecked) {
				this.selectAllChecked = false
				this.selectedField = []
			} else {
				this.selectAllChecked = true
				const availableFields = [...this.mappedAvailableFields]
				if (this.searchInput) {
					this.selectedField = availableFields.filter(field => field.text.toLowerCase().includes(this.searchInput.toLowerCase())).map(field => field.text)
				} else {
					this.selectedField = availableFields.map(field => field.text)
				}
			}
		},
		updateSelectAll () {
			const availableFields = [...this.mappedAvailableFields]
			const filteredFields = this.searchInput
				? availableFields.filter(field => field.text.toLowerCase().includes(this.searchInput.toLowerCase()))
				: availableFields
			this.selectAllChecked = this.selectedField.length === filteredFields.length
		}
	}
}
</script>
